import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TooltipContent, TooltipProvider, TooltipTrigger, Tooltip as TooltipUI } from '@/components/ui/tooltip';
import { useDashboard } from '@/contexts/DashboardContext';
import { formatTrendsXAxisLabel, groupDataByRangeType } from '@/lib/utils';
import { LINE_CHART_COLORS } from '@/constants/colors';
import { WidgetData } from '@/types';
import { Info } from 'lucide-react';
import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { GRID_CONFIG } from '@/lib/chartConfig';

interface SlaTrendWidgetProps {
  data: WidgetData;
  isGeneralMode?: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
  error?: string;
  rangeType?: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
}

interface SlaTrendRawDataPoint {
  time: string;
  totalSla: number;
  totalAnsweredCall: number;
}

interface TooltipPayloadItem {
  value: number;
  dataKey: string;
  name: string;
  color: string;
  payload: {
    time: string;
    slaPercentage: number;
  };
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  theme?: string;
  rangeType?: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
}

const CustomTooltip = ({ active, payload, label, theme, rangeType }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div className={`border rounded-lg shadow-md p-3 min-w-[220px] ${theme === "dark"
        ? "bg-gray-800 border-gray-600 text-white"
        : "bg-white border-gray-200 text-gray-900"
        }`}>
        {/* <div className="font-semibold mb-1">{formatTrendsXAxisLabel(label, rangeType)}</div> */}
        <div style={{ color: theme === "dark" ? LINE_CHART_COLORS.TOOLTIP_TEXT.dark : LINE_CHART_COLORS.TOOLTIP_TEXT.light }} className="text-sm">SLA: {payload[0].value}%</div>
      </div>
    );
  }
  return null;
};

const SlaTrendWidget: React.FC<SlaTrendWidgetProps> = ({ data, icon, loading, error, rangeType = 'weekly' }) => {
  const { theme } = useDashboard();

  const chartData = useMemo(() => {
    if (!data || !Array.isArray(data.value) || data.value.length === 0) {
      return [];
    }

    // Calculate SLA percentage
    const finalData = (data.value as SlaTrendRawDataPoint[]).map(item => ({
      ...item,
      slaPercentage: item.totalAnsweredCall > 0 ? Math.round((item.totalSla / item.totalAnsweredCall) * 100) : 0,
    }));

    return finalData;
  }, [data]);

  const renderContent = () => {
    if (loading) {
      return <Skeleton className="w-full h-[300px] rounded-none" />;
    }

    if (error) {
      return <div className="text-red-500 text-center py-10">{error}</div>;
    }

    if (chartData.length === 0) {
      return (
        <div className="flex items-center justify-center h-[300px] text-sm text-muted-foreground dark:text-white/60">
          <div className="flex flex-col items-center gap-2">
            <span>No call records were found</span>
          </div>
        </div>
      );
    }

    // Check if all data points are 0
    const isAllDataZero = chartData?.every(item =>
      item.slaPercentage === 0 || item.slaPercentage == null
    ) ?? false;

    return (
      <ResponsiveContainer width="99%" height={280}>
        <LineChart data={chartData} margin={{ left: 0, right: 20, top: 20, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="time"
            tickFormatter={(label) => formatTrendsXAxisLabel(label, rangeType)}
            tick={{ fontSize: 14, fill: theme === "dark" ? '#fff' : '#6b7280' }}
          />
          <YAxis
            tick={{ fontSize: 14, fill: theme === "dark" ? '#fff' : '#6b7280' }}
            domain={isAllDataZero ? [0, 100] : undefined}
            ticks={isAllDataZero ? [0, 20, 40, 60, 80, 100] : undefined}
          />
          <Tooltip content={<CustomTooltip theme={theme} rangeType={rangeType} />} cursor={{ stroke: theme === "dark" ? '#4b5563' : '#d1d5db' }}/>
          {!isAllDataZero && (
            <Line
              type="monotone"
              dataKey="slaPercentage"
              name="SLA (%)"
              stroke={theme === "dark" ? LINE_CHART_COLORS.LINE.dark : LINE_CHART_COLORS.LINE.light}
              strokeWidth={2}
              dot={{ r: 5, stroke: theme === "dark" ? LINE_CHART_COLORS.LINE.dark : LINE_CHART_COLORS.LINE.light, strokeWidth: 2, fill: theme === "dark" ? LINE_CHART_COLORS.DOT_FILL.dark : LINE_CHART_COLORS.DOT_FILL.light }}
              activeDot={{ r: 7, fill: theme === "dark" ? LINE_CHART_COLORS.LINE.dark : LINE_CHART_COLORS.LINE.light, stroke: theme === "dark" ? LINE_CHART_COLORS.LINE.dark : LINE_CHART_COLORS.LINE.light, strokeWidth: 2 }}
              connectNulls={true}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Card className="w-full overflow-hidden min-h-[300px] dark:border-0">
      <CardHeader>
        <div className="flex items-center gap-2">
          <span>SLA (%) Trend</span>
          <TooltipProvider>
            <TooltipUI delayDuration={0}>
              <TooltipTrigger asChild>
                <span className="cursor-pointer">
                  <Info className="h-4 w-4 text-muted-foreground" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="max-w-xs text-xs font-normal">
                  The pattern according to the percentage of total incoming calls handled within 60 seconds within the given time.
                </p>
              </TooltipContent>
            </TooltipUI>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="relative overflow-hidden">
          {renderContent()}
        </div>
      </CardContent>
    </Card>
  );
};

export default SlaTrendWidget;
